import { useEffect, useState } from 'react'
import moment from 'moment'
import serviceInterface from '../../../services/service.interface'
import {
  resolveGoalCell,
  PROGRESS_GOAL_MAX_WEEK,
} from '../../schedule/progress.goals.defaults'

// Resolve the patient's configured Progress-Test ROM goal (clinical degrees:
// 0° = full extension, higher = deeper flexion) PER DATE, driving the green
// "target" line on the TracPatch Flexion / Extension charts. The goal is a
// weekly step function of post-op week, so each date on the chart uses the goal
// for that date's week — the line steps as the chart spans multiple weeks
// rather than sitting flat at the current week's value.
//
// Single source of truth — same precedence the admin "Schedule & Progress-Test
// Goals" editor (progress.goals.modal.js) and the patient app use:
//   per-patient override (patient.progressTestGoalOverrides)
//     → clinic setting ('progress-test-goals')
//       → hardcoded default table (DEFAULT_PROGRESS_WEEK_GOALS)
//
// The clinic layer is fetched best-effort: if there's no customer id (e.g. the
// patient/caregiver iframe context) or the call fails, resolution falls back to
// per-patient overrides + the default table.
//
// Returns:
//   surgeryDate          — the patient's surgery date (null → no goal line)
//   weekForDate(date)    — post-op week for a date (-1 = pre-op)
//   goalForDate(date, f) — resolved goal (clinical degrees) for a date + field
//   signature            — primitive that changes when inputs change (use as a
//                          chart redraw dep so the line repaints once the clinic
//                          fetch resolves)
export function useRomGoalTarget(patient) {
  const [clinicOverrides, setClinicOverrides] = useState([])

  const customerid =
    (patient &&
      patient.customer &&
      (patient.customer._id || patient.customer)) ||
    null

  useEffect(() => {
    let cancelled = false
    if (!customerid) {
      setClinicOverrides([])
      return undefined
    }
    serviceInterface
      .SettingsService()
      .getClinicSetting({ customerid, key: 'progress-test-goals' })
      .then((res) => {
        if (cancelled) return
        const value = res && res.data && res.data.value
        const list =
          value && Array.isArray(value.overrides) ? value.overrides : []
        setClinicOverrides(list)
      })
      .catch(() => {
        if (!cancelled) setClinicOverrides([])
      })
    return () => {
      cancelled = true
    }
  }, [customerid])

  const overrides = (patient && patient.progressTestGoalOverrides) || []
  const surgeryDate = patient && patient.surgery && patient.surgery.date

  // Post-op week for an arbitrary date: pre-op (< surgery) → -1, otherwise
  // floor(daysSince / 7) clamped to the last configured week. Mirrors
  // currentWeekFromSurgery() in tracpatch.week.defaults but for any date.
  const weekForDate = (date) => {
    if (!surgeryDate) return null
    const days = moment(date)
      .startOf('day')
      .diff(moment(surgeryDate).startOf('day'), 'days')
    if (days < 0) return -1
    return Math.min(PROGRESS_GOAL_MAX_WEEK, Math.floor(days / 7))
  }

  const goalForDate = (date, field) => {
    const w = weekForDate(date)
    if (w == null) return null
    return resolveGoalCell(w, field, overrides, clinicOverrides)
  }

  const signature = JSON.stringify({ surgeryDate, overrides, clinicOverrides })

  return { surgeryDate, weekForDate, goalForDate, signature }
}
